import React from 'react';
import axios from 'axios';
import ReactDOM from 'react-dom/client';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { SERVER_BASE_URL } from './redux/apiConstants';
import { getCookies, setCookies, clearUserCookiesOnLogout } from './hooks/useCookies';
import envValues from './enviornment';
import { SESSION_EXPIRED_KEY } from './constants/sessionConstants';

const LOGIN_PATH = '/';
const REFRESH_URL = `${SERVER_BASE_URL}/auth/refresh-tokens`;

/**
 * The API rejects expired tokens with 401 "Please authenticate." — note the
 * trailing period. Match loosely so punctuation or casing never breaks refresh.
 */
const isAuthMessage = (message) => /please authenticate/i.test(String(message || ''));

/** One shared refresh request. The API deletes the old refresh token when it issues
 * a new pair, so parallel refreshes would invalidate each other and log the user out. */
let refreshPromise = null;

const refreshSession = () => {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(
        REFRESH_URL,
        { refreshToken: getCookies('refreshToken') },
        {
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
            'Access-Control-Allow-Origin': '*',
          },
        }
      )
      .then((rs) => {
        // v2 responds { data: { tokens: { access, refresh } } }
        const tokens = rs?.data?.data?.tokens || rs?.data?.data;
        const accessToken = tokens?.access?.token;
        const nextRefreshToken = tokens?.refresh?.token;
        if (!accessToken) {
          throw new Error('Refresh response did not contain an access token.');
        }
        setCookies('Token', accessToken);
        if (nextRefreshToken) setCookies('refreshToken', nextRefreshToken);
        axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        return accessToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

/** Refresh token is gone or rejected: end the session cleanly instead of leaving
 * the user on a page that keeps toasting raw "Please authenticate." errors. */
let sessionExpiryHandled = false;
const handleSessionExpired = () => {
  if (sessionExpiryHandled) return;
  sessionExpiryHandled = true;
  clearUserCookiesOnLogout();
  if (window.location.pathname !== LOGIN_PATH) {
    try {
      sessionStorage.setItem(SESSION_EXPIRED_KEY, 'true');
    } catch (_) {
      // sessionStorage may be unavailable in some sandboxed environments
    }
    window.location.assign(LOGIN_PATH);
  }
};

axios.interceptors.request.use(
  (config) => config,
  (err) => {
    if (err?.response?.status === 403) {
      window.location.assign(envValues.REACT_APP_API_PERMISSION_DENIED);
    }
    return Promise.reject(err);
  }
);

axios.interceptors.response.use(
  async (res) => {
    if (res.status === 403) {
      window.location.assign(envValues.REACT_APP_API_PERMISSION_DENIED);
    }
    return res;
  },
  async (err) => {
    const status = err?.response?.status;
    const originalConfig = err?.config;
    const authError = status === 401 && isAuthMessage(err?.response?.data?.message);
    const isRefreshCall = String(originalConfig?.url || '').includes('/auth/refresh-tokens');

    // Guests have no token, so a 401 on a public page must not end in a redirect.
    const hadSession = Boolean(getCookies('Token'));

    if (
      authError &&
      hadSession &&
      !isRefreshCall &&
      originalConfig &&
      !originalConfig.retriedAfterRefresh
    ) {
      originalConfig.retriedAfterRefresh = true;

      // Many pages capture the token once on mount. If the cookie has moved on
      // since this request was built, another call already refreshed it — retry
      // with the current token instead of rotating the refresh token again.
      const sentToken = String(originalConfig.headers?.Authorization || '').replace(/^Bearer /, '');
      const currentToken = getCookies('Token');
      if (sentToken && currentToken && sentToken !== currentToken) {
        originalConfig.headers = {
          ...originalConfig.headers,
          Authorization: `Bearer ${currentToken}`,
        };
        return axios(originalConfig);
      }

      // Only worth retrying if we still hold a refresh token to trade in.
      if (!getCookies('refreshToken')) {
        handleSessionExpired();
        return Promise.reject(err);
      }
      try {
        const accessToken = await refreshSession();
        originalConfig.headers = {
          ...originalConfig.headers,
          Authorization: `Bearer ${accessToken}`,
        };
        return await axios(originalConfig);
      } catch (refreshError) {
        handleSessionExpired();
        return Promise.reject(refreshError);
      }
    }

    if (authError && hadSession && (isRefreshCall || originalConfig?.retriedAfterRefresh)) {
      handleSessionExpired();
    } else if (status === 403) {
      window.location.assign(envValues.REACT_APP_API_PERMISSION_DENIED);
    }
    return Promise.reject(err);
  }
);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

reportWebVitals();
